import { defineStore } from 'pinia';
import { apolloClient } from '@/utils/apollo';
import { useDepartmentStore } from '@/stores/department';
import { cloneDeep } from 'lodash';
import { format } from '@/utils/date-fns';
import { pcdMealsQuery } from '@/graphql/queries/pcdMeals.js';
import { mealSignupScreenMutation } from '@/graphql/mutations/mealSignupScreen.js';
import { mealCancelSignupScreenMutation } from '@/graphql/mutations/mealCancelSignupScreen.js';
import { useInstitutionStore } from '@/stores/institution';
import { useProfilesStore } from '@/stores/profiles';
import { getUniqueInstitutions } from '@/utils/helpers';
import {
  IDepartment,
  IDocument,
  IImage,
  IInstitution,
  IProfile,
  IVideo,
} from '@/interfaces';
import api from '@/api';
import { MealEntry } from '@/graphql/custom-types';

export interface IDish {
  description: string;
  picture: IImage;
  title: string;
}
export interface IMeal {
  id: string;
  title: string;
  category: string;
  startDate: string | number | Date;
  endDate?: string | number | Date;
  coverImage: {
    source: string;
  };
  participants: IProfile[];
  signupPossible: boolean;
  signupEnd: string;
  cancelled: boolean;
  pictures: IImage[];
  institution: IInstitution;
  departments: IDepartment[];
  videos?: IVideo[];
  isPcd?: boolean;
  documents?: IDocument[];
  dishes?: IDish[];
  description: string;
}

export interface IMealState {
  all: MealEntry[];
  selected: MealEntry;
  uniqueInstitutions: IInstitution[];
}

const DEFAULT_MEAL: MealEntry = {
  __typename: 'SingleMeal',
  id: '',
  title: '',
  startDate: '',
  endDate: '',
  coverImage: {
    __typename: 'Picture',
    id: '',
    source: '',
    blurhash: '',
  },
  participants: [],
  signupPossible: false,
  signupEnd: '',
  cancelled: false,
  pictures: [],
  institution: {
    __typename: 'Institution',
    id: '',
    name: '',
    color: '',
    logo: {
      __typename: 'Picture',
      id: '',
      source: '',
    },
  },
  departments: [],
  dishes: [],
  description: '',
  maxSignups: null,
  signupStart: null,
  documents: null,
  responsible: null,
};

export const useMealsStore = defineStore('meals', {
  state: (): IMealState & { allCopy: MealEntry[] } => ({
    all: [],
    selected: DEFAULT_MEAL,
    uniqueInstitutions: [],
    allCopy: [],
  }),
  actions: {
    async getMeals() {
      const departmentStore: any = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      const dateNow = new Date();
      const daysForward = departmentStore.settings?.meal?.daysForward || 90;

      const dateStart = dateNow.setDate(dateNow.getDate() + daysForward - 1); // subtract one as we should include today's date
      const dateEnd = new Date(dateStart);

      api.meal
        .getMealRange(departmentStore.id, dateEnd)
        .then((mealRange) => {
          this.all = [...mealRange];
          this.allCopy = [...mealRange];
          this.uniqueInstitutions = getUniqueInstitutions(mealRange);
        })
        .catch((error) => {
          console.log('Error getting meals', error);
        });
    },
    async getPcdMeals() {
      const departmentStore = useDepartmentStore();

      if (!departmentStore.id) {
        throw new Error('No department id provided');
      }

      await apolloClient
        .query({
          query: pcdMealsQuery,
          fetchPolicy: 'no-cache',
          variables: {
            date: format(new Date(), 'yyyy-MM-dd'),
          },
        })
        .then((response) => {
          const meals = response.data.pcdMeals.reduce((acc: any, meal: any) => {
            const courses = meal.courses.map((course: any) => {
              return {
                isPcd: true,
                id: course.id,
                title: course.title,
                category: course.category,
                startDate: meal.date,
                coverImage: {
                  source: '/img/external/external-meal.jpg',
                },
              };
            });

            acc.push(...courses);
            return acc;
          }, []);

          this.all = meals;
        })
        .catch((error) => {
          console.log('Error getting meals', error);
        });
    },
    async getSelectedMeal(mealId: string) {
      const institutionStore: any = useInstitutionStore();

      if (institutionStore.settings.useExternalMeals) {
        const meal =
          this.all.find((meal) => meal.id === mealId) || DEFAULT_MEAL;
        this.selected = meal;
        return;
      }

      await api.meal
        .getMealById(mealId)
        .then((mealEntry) => {
          this.selected = mealEntry;
        })
        .catch((error) => {
          console.log('Could not get meal entry: ', error);
        });
    },
    async mealSignupScreen({ subscription, profile, participating }: any) {
      const profilesStore = useProfilesStore();
      const mutation = participating
        ? mealSignupScreenMutation
        : mealCancelSignupScreenMutation;

      await apolloClient
        .mutate({
          mutation,
          variables: {
            subscription,
            profile,
          },
        })
        .then(async () => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: true,
          });

          this.getSelectedMeal(subscription.id);
        })
        .catch((error) => {
          profilesStore.setPinValidation({
            validating: false,
            validPin: false,
          });

          throw new Error('Error signing up to meal', error);
        });
    },
    async updateParticipants(mealId: string) {
      await api.meal
        .getMealById(mealId)
        .then((mealEntry) => {
          const participants = mealEntry.participants;

          const allMeals = cloneDeep(this.all);
          const mealIndex = allMeals.findIndex((meal) => meal.id === mealId);

          if (mealIndex > -1) {
            allMeals[mealIndex].participants = participants;
            this.all = allMeals;
          }
        })
        .catch((error) => {
          console.log('Error: ', error);
        });
    },
  },
});
